import React, { useState, useEffect, useRef } from "react";
import "../ChatWindow.css";
import {
  getQuestions,
  getSubjects,
  getTopicsAndSubtopics,
  evaluateAnswers,
} from "../services/mcqApi";

const parseQuestionComponents = (question) => {
  const qObj = question?.question || {};
  let stem = question?.stem || qObj?.stem || "";
  let statements =
    Array.isArray(question?.statements) && question.statements.length > 0
      ? question.statements
      : Array.isArray(qObj?.statements) && qObj.statements.length > 0
      ? qObj.statements
      : [];
  let instruction = question?.instruction || qObj?.instruction || "";

  // If statements were not in q_json, attempt to parse from title if title contains statements
  if (!stem && question?.title) {
    const raw = String(question.title);
    const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
    const stmtLines = [];
    const stemLines = [];
    const instrLines = [];
    let state = "stem";

    for (const line of lines) {
      if (/^\d+[\.\)]\s+/.test(line)) {
        state = "stmt";
        stmtLines.push(line.replace(/^\d+[\.\)]\s+/, ""));
      } else if (
        state === "stmt" &&
        /(which of the|select the|choose the|how many of the)/i.test(line)
      ) {
        state = "instr";
        instrLines.push(line);
      } else if (state === "stmt") {
        stmtLines.push(line);
      } else {
        stemLines.push(line);
      }
    }

    if (stmtLines.length > 0) {
      stem = stemLines.join("<br />");
      statements = stmtLines;
      instruction = instrLines.join("<br />");
    } else {
      stem = raw.replace(/\n/g, "<br />");
    }
  }

  return { stem, statements, instruction };
};

const McqResponse = ({
  setMessages,
  sender,
  message,
  index,
  chatUniqueIndex,
  isStreamingDone,
  mcqData,
  isScrolling,
  setIsScrolling,
}) => {
  const [demo, setdemo] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mcqQuestions, setMcqQuestions] = useState(null);
  const [mcqResults, setMcqResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subjectId, setSubjectId] = useState(null);
  const [topicId, setTopicId] = useState(null);
  const [subtopicId, setSubtopicId] = useState(null);
  const [click, setclick] = useState(false);

  // Auto-scroll functionality
  const messagesStartRef = useRef(null);
  const contentRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);

  const handleUserScroll = () => {
    setIsUserScrolling(true);
    // Reset after some time
    setTimeout(() => {
      setIsUserScrolling(false);
    }, 2000);
  };

  // Auto-scroll to new content
  useEffect(() => {
    if (messagesStartRef.current && !isScrolling) {
      const rect = messagesStartRef.current.getBoundingClientRect();
      if (rect.top > 0) {
        messagesStartRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  });

  // Scroll to bottom for bot responses during streaming
  useEffect(() => {
    if (sender === "bot" && contentRef.current && mcqQuestions) {
      const responseDiv = contentRef.current;
      responseDiv.scrollTop = responseDiv.scrollHeight;
    }
  }, [mcqQuestions, isSubmitted]);

  // Add scroll listener
  useEffect(() => {
    const container = document.querySelector(".chat-window-container");
    if (container) {
      container.addEventListener("scroll", handleUserScroll);
      return () => {
        container.removeEventListener("scroll", handleUserScroll);
      };
    }
  }, []);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        //  console.log("questions lenght in mcq questions lenght in mcq")
        if (mcqData.subjectId != null) {
          const questions = await getQuestions(
            mcqData.subjectId,
            mcqData.topicId,
            mcqData.subtopicId,
            parseInt(mcqData.numQuestions),
          );
          setMcqQuestions(questions);
        } else {
          const questions = mcqData.questions;
          setMcqQuestions(questions);
        }

        //           if (questions.length === 0) {
        //             console.log("knfdlnnlnlnmlnlfd lknled")
        //             setdemo(true);
        //  setMessages(prev => [
        //     ...prev,
        //     {
        //       sender: "bot",
        //       message: "Sorry, there are no MCQs available for this topic. Please select another one.",
        //       prompt: "Prelims Practice",
        //       mcqData: null
        //     }
        //   ]);
        // } else {
        //   setMcqQuestions(questions);
        // }
      } catch (error) {
        console.error("Error loading MCQ questions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const handleOptionSelect = (questionId, optionId) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  const handleSubmit = async () => {
    setclick(true);

    try {
      // Format answers for API
      const formattedAnswers = Object.entries(selectedAnswers).map(
        ([questionId, answer]) => ({
          question_id: parseInt(questionId),
          selected_answer: answer,
        }),
      );

      const evaluationResult = await evaluateAnswers(formattedAnswers);

      // Process the evaluation result
      let newResults = {};
      let newScore = 0;
      let attempted = 0;
      let correctCount = 0;
      let incorrectCount = 0;

      evaluationResult.results.forEach((result) => {
        const question = mcqQuestions.find((q) => q.id === result.question_id);
        if (question) {
          newResults[result.question_id] = {
            selected: selectedAnswers[result.question_id] || null,
            correct: result.correct_answer,
            isCorrect: result.is_correct,
            explanation: result.explanation,
          };

          if (selectedAnswers[result.question_id]) {
            attempted++;
            if (result.is_correct) {
              correctCount++;
              newScore += 2;
            } else {
              incorrectCount++;
              newScore -= 0.66;
            }
          }
        }
      });

      setMcqResults({
        questions: newResults,
        score: {
          total: mcqQuestions.length,
          correct: correctCount,
          incorrect: incorrectCount,
          accuracy: ((correctCount / attempted) * 100).toFixed(2),
        },
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error evaluating answers:", error);
    }
  };

  const getOptionStyle = (questionId, optionId) => {
    if (!isSubmitted) {
      return selectedAnswers[questionId] === optionId ? "selected-option" : "";
    }
    const question = mcqResults.questions[questionId];
    if (question.correct === optionId) {
      return "correct-option";
    }
    if (selectedAnswers[questionId] === optionId) {
      return "incorrect-option";
    }
    return "";
  };

  if (loading) {
    return <div className="loading">Loading questions...</div>;
  }

  return (
    <div className={`message w-100 ${sender === "bot" ? "bot" : "user"}`}>
      {!isScrolling && <div ref={messagesStartRef}></div>}
      <div ref={contentRef} className={`message-content ${chatUniqueIndex}`}>
        {sender === "user" ? (
          <div className="user-message chat-bubble user  mcq-bot-message">
            <p>{message}</p>
          </div>
        ) : (
          <>
            {/* <div className="user-message chat-bubble user mcq-bot-message">
              <p>{message}</p>
            </div> */}

            {mcqQuestions == null ? null : mcqQuestions.length != 0 ? (
              <div className="mcq-container">
                <div className="mcq-header">
                  <h3>MCQ Practice Test</h3>
                  <p>
                    Answer all questions and click submit to see your results.
                  </p>
                </div>

                <div className="mcq-questions">
                  {mcqQuestions.map((question, index) => {
                    let answersObj;
                    try {
                      answersObj =
                        typeof question.answers === "string"
                          ? JSON.parse(question.answers)
                          : question.answers;
                    } catch (e) {
                      console.error(
                        "Error parsing answers for question",
                        question.id,
                        question.answers,
                      );
                      answersObj = {};
                    }
                    const { stem, statements, instruction } = parseQuestionComponents(question);
                    return (
                      <div key={question.id} className="mcq-question">
                        <div className="mcq-question-header">
                          <span className="question-num">Question {index + 1}:</span>
                          {(question.subject || question.year) && (
                            <span className="subject-year">
                              {question.subject && (
                                <span className="que-con" style={{ marginRight: question.year ? "6px" : "0" }}>
                                  {question.subject}
                                </span>
                              )}
                              {question.year && <span className="que-con">{question.year}</span>}
                            </span>
                          )}
                        </div>
                        <div className="question-content">
                          {stem && (
                            <div
                              className="question-stem"
                              dangerouslySetInnerHTML={{ __html: stem }}
                            />
                          )}
                          {statements.length > 0 && (
                            <div className="question-statements">
                              {statements.map((stmt, stmtIdx) => (
                                <div key={stmtIdx} className="question-statement">
                                  <span className="question-statement-index">{stmtIdx + 1}.</span>
                                  <span
                                    className="question-statement-text"
                                    dangerouslySetInnerHTML={{ __html: String(stmt ?? "") }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          {instruction && (
                            <div
                              className="question-instruction"
                              dangerouslySetInnerHTML={{ __html: instruction }}
                            />
                          )}
                        </div>
                        <div className="options-container">
                          {Object.entries(answersObj).map(
                            ([optionId, optionText]) => (
                              <div
                                key={optionId}
                                className={`option ${getOptionStyle(
                                  question.id,
                                  optionId,
                                )}`}
                                onClick={() =>
                                  !isSubmitted &&
                                  handleOptionSelect(question.id, optionId)
                                }
                              >
                                <span
                                  className="option-text"
                                  dangerouslySetInnerHTML={{
                                    __html: String(optionText ?? ""),
                                  }}
                                />
                              </div>
                            ),
                          )}
                        </div>
                        {isSubmitted &&
                          mcqResults &&
                          selectedAnswers[question.id] !==
                            mcqResults.questions[question.id].correct && (
                            <div className="explanation">
                              <strong>Explanation:</strong>
                              <div
                                className="explanation-content"
                                dangerouslySetInnerHTML={{
                                  __html: mcqResults.questions[question.id].explanation,
                                }}
                              />
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>

                {!isSubmitted ? (
                  <button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={
                      Object.keys(selectedAnswers).length !==
                        mcqQuestions.length || click
                    }
                  >
                    {click ? <span>Submitting...</span> : <span>Submit</span>}
                  </button>
                ) : (
                  mcqResults && (
                    <div className="mcq-results-card">
                      <div className="mcq-results-header">
                        <img
                          src="/assets/images/logo/yooki_navigator.svg"
                          alt="Results"
                          className="mcq-results-icon"
                        />
                        <span className="mcq-results-title">Your Test Results Are In!</span>
                      </div>
                      <div className="mcq-results-stats">
                        <div className="mcq-stat-item">
                          <span className="mcq-stat-label">Score</span>
                          <span className="mcq-stat-val">
                            {mcqResults.score.correct} <span className="mcq-stat-total">/ {mcqResults.score.total}</span>
                          </span>
                        </div>
                        <div className="mcq-stat-item">
                          <span className="mcq-stat-label">Accuracy</span>
                          <span className="mcq-stat-val">{mcqResults.score.accuracy}%</span>
                        </div>
                        <div className="mcq-stat-item">
                          <span className="mcq-stat-label">Correct</span>
                          <span className="mcq-stat-val mcq-val-correct">{mcqResults.score.correct}</span>
                        </div>
                        <div className="mcq-stat-item">
                          <span className="mcq-stat-label">Incorrect</span>
                          <span className="mcq-stat-val mcq-val-incorrect">{mcqResults.score.incorrect}</span>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div>
                Sorry, there are no MCQs available for this topic. Please select
                another one
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default McqResponse;
